import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { pointThresholdsService } from '../../../services/supabaseService';

const PointThresholdsSection = () => {
  const [thresholds, setThresholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', points: '', action: '', color: '', icon: '' });

  // Load point thresholds from Supabase on component mount
  useEffect(() => {
    loadThresholds();
  }, []);

  const loadThresholds = async () => {
    try {
      setLoading(true);
      const data = await pointThresholdsService?.getAll();
      setThresholds(data);
    } catch (error) {
      console.error('Error loading point thresholds:', error);
    } finally {
      setLoading(false);
    }
  };

  const actionOptions = [
    { value: 'Email Notification', label: 'Email Notification' },
    { value: 'Formal Written Warning', label: 'Formal Written Warning' },
    { value: 'Suspension Consideration', label: 'Suspension Consideration' },
    { value: 'Termination Consideration', label: 'Termination Consideration' },
    { value: 'Manager Review Required', label: 'Manager Review Required' },
    { value: 'HR Intervention', label: 'HR Intervention' }
  ];

  const colorOptions = [
    { value: 'success', label: 'Success (Green)' },
    { value: 'warning', label: 'Warning (Amber)' },
    { value: 'error', label: 'Error (Red)' },
    { value: 'destructive', label: 'Critical (Dark Red)' }
  ];

  const iconOptions = [
    { value: 'AlertCircle', label: 'Alert Circle' },
    { value: 'FileText', label: 'File Text' },
    { value: 'PauseCircle', label: 'Pause Circle' },
    { value: 'XCircle', label: 'X Circle' },
    { value: 'public/assets/images/Real_logo-1771587686730.png', label: 'public/assets/images/Real_logo-1771587686730.png' },
    { value: 'AlertTriangle', label: 'Alert Triangle' }
  ];

  const getColorClass = (color) => {
    const colors = {
      'success': 'bg-success/10 border-success text-success',
      'warning': 'bg-warning/10 border-warning text-warning',
      'error': 'bg-error/10 border-error text-error',
      'destructive': 'bg-destructive/10 border-destructive text-destructive'
    };
    return colors?.[color] || 'bg-muted border-border text-foreground';
  };

  const handleEdit = (threshold) => {
    setEditingId(threshold?.id);
    setFormData({
      name: threshold?.name,
      points: threshold?.points,
      action: threshold?.action,
      color: threshold?.color,
      icon: threshold?.icon
    });
  };

  const handleSave = async () => {
    try {
      const updatedThreshold = await pointThresholdsService?.update(editingId, {
        ...formData,
        points: parseFloat(formData?.points)
      });
      if (updatedThreshold) {
        setThresholds(prev => prev?.map(t => t?.id === editingId ? updatedThreshold : t));
        setEditingId(null);
        setFormData({ name: '', points: '', action: '', color: '', icon: '' });
      }
    } catch (error) {
      console.error('Error saving point threshold:', error);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({ name: '', points: '', action: '', color: '', icon: '' });
  };

  const maxPoints = Math.max(...thresholds?.map(t => t?.points));

  return (
    <div className="p-6">
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-sm text-muted-foreground">Loading point thresholds...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-6">
            <h2 className="font-heading font-semibold text-xl text-foreground mb-1">
              Point Thresholds
            </h2>
            <p className="font-body text-sm text-muted-foreground">
              Configure warning levels, intervention triggers, and automatic action thresholds
            </p>
          </div>

          {/* Visual Threshold Indicator */}
          <div className="bg-muted/30 rounded-lg p-6 mb-6">
            <h3 className="font-heading font-semibold text-base text-foreground mb-4">
              Threshold Visualization
            </h3>
            <div className="relative">
              <div className="h-3 bg-gradient-to-r from-success via-warning via-error to-destructive rounded-full" />
              <div className="relative mt-4">
                {thresholds?.sort((a, b) => a?.points - b?.points)?.map((threshold, index) => (
                  <div
                    key={threshold?.id}
                    className="absolute transform -translate-x-1/2"
                    style={{ left: `${(threshold?.points / maxPoints) * 100}%` }}
                  >
                    <div className="flex flex-col items-center">
                      <div className={`w-4 h-4 rounded-full border-2 bg-card ${getColorClass(threshold?.color)}`} />
                      <div className="mt-2 text-center">
                        <div className="font-data font-semibold text-sm text-foreground">
                          {threshold?.points} pts
                        </div>
                        <div className="text-xs text-muted-foreground whitespace-nowrap">
                          {threshold?.name}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Threshold Configuration */}
          <div className="space-y-4">
            {thresholds?.sort((a, b) => a?.points - b?.points)?.map((threshold) => (
              <div
                key={threshold?.id}
                className={`rounded-lg border-2 p-4 transition-smooth ${getColorClass(threshold?.color)}`}
              >
                {editingId === threshold?.id ? (
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <Input
                        label="Threshold Name"
                        value={formData?.name}
                        onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                      />
                      <Input
                        label="Point Value"
                        type="number"
                        step="1"
                        min="0"
                        value={formData?.points}
                        onChange={(e) => setFormData({ ...formData, points: e?.target?.value })}
                      />
                      <Select
                        label="Automatic Action"
                        options={actionOptions}
                        value={formData?.action}
                        onChange={(value) => setFormData({ ...formData, action: value })}
                      />
                      <Select
                        label="Color Theme"
                        options={colorOptions}
                        value={formData?.color}
                        onChange={(value) => setFormData({ ...formData, color: value })}
                      />
                      <Select
                        label="Icon"
                        options={iconOptions}
                        value={formData?.icon}
                        onChange={(value) => setFormData({ ...formData, icon: value })}
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={handleCancel}>
                        Cancel
                      </Button>
                      <Button variant="default" size="sm" onClick={handleSave}>
                        Save Changes
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-card">
                        <Icon name={threshold?.icon} size={24} />
                      </div>
                      <div>
                        <h4 className="font-heading font-semibold text-base mb-1">
                          {threshold?.name}
                        </h4>
                        <p className="text-sm opacity-90">
                          Triggered at <span className="font-data font-semibold">{threshold?.points} points</span>
                        </p>
                        <p className="text-xs opacity-75 mt-1">
                          Action: {threshold?.action}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit2"
                      onClick={() => handleEdit(threshold)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-6 bg-muted/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-primary mt-0.5" />
              <div>
                <h4 className="font-heading font-semibold text-sm text-foreground mb-1">
                  Threshold Configuration Tips
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Thresholds trigger automatic actions when employee points reach specified levels</li>
                  <li>• Configure email notifications, manager alerts, and HR interventions</li>
                  <li>• Visual indicators help managers quickly identify at-risk employees</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PointThresholdsSection;